import React from "react";
import styled from "styled-components";
import { spacing, typography } from "../../styles/designSystem";
import { SubsectionHeadline } from "../Typography";

const Section = styled.section`
    margin: ${spacing[900]} 0;
`;

const SectionTitle = styled(SubsectionHeadline)`
    margin: 0 0 ${spacing[400]} 0;
    padding-bottom: ${spacing[300]};
    border-bottom: 4px solid ${({ accentColor, theme }) => accentColor || theme.primary};
`;

const SectionContent = styled.div`
    margin-top: ${spacing[600]};
`;

const Paragraph = styled.p`
    font-size: ${typography.fontSize[400]};
    line-height: ${typography.lineHeight.relaxed};
    color: ${({ theme }) => theme.text};
    margin: 0 0 ${spacing[500]} 0;

    &:last-child {
        margin-bottom: 0;
    }
`;

const Highlight = styled.div`
    background: ${({ theme, accentColor }) =>
        accentColor ? `${accentColor}12` : `${theme.primary}12`};
    border: 1px solid ${({ theme, accentColor }) => accentColor || theme.primary};
    border-left-width: 2px;
    padding: ${spacing[500]};
    margin: ${spacing[600]} 0;
`;

const HighlightText = styled.p`
    font-size: ${typography.fontSize[300]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.text};
    margin: 0;

    &::before {
        content: "> ";
        color: ${({ theme }) => theme.primary};
        opacity: 0.75;
    }
`;

const BulletList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: ${spacing[500]};
`;

const BulletItem = styled.li`
    display: flex;
    align-items: flex-start;
    gap: ${spacing[400]};
    font-size: ${typography.fontSize[400]};
    line-height: ${typography.lineHeight.relaxed};
    color: ${({ theme }) => theme.text};

    &::before {
        content: "\\25B8";
        color: ${({ accentColor, theme }) => accentColor || theme.primary};
        flex-shrink: 0;
        line-height: inherit;
    }
`;

const BulletContent = styled.span`
    flex: 1;
`;

const BulletTitle = styled.strong`
    display: block;
    margin-bottom: ${spacing[200]};
    font-family: ${typography.fontFamily.display};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    color: ${({ theme }) => theme.primary};
    font-weight: ${typography.fontWeight.semibold};
`;

const SectionBlock = ({ title, paragraphs, highlight, accentColor, children }) => {
    // Check if paragraphs contain bullet points (start with • or - or *)
    const hasBulletPoints = paragraphs?.some(para =>
        typeof para === 'string' && (para.trim().startsWith('•') || para.trim().startsWith('-') || para.trim().startsWith('*'))
    );

    const renderContent = () => {
        if (hasBulletPoints) {
            return (
                <BulletList>
                    {paragraphs.map((para, index) => {
                        // Remove the bullet character and trim
                        const cleanText = para.replace(/^[•\-*]\s*/, '').trim();

                        // Check if there's a colon to split title and description
                        const colonIndex = cleanText.indexOf(':');
                        if (colonIndex > 0) {
                            const bulletTitle = cleanText.substring(0, colonIndex);
                            const bulletDesc = cleanText.substring(colonIndex + 1).trim();
                            return (
                                <BulletItem key={index} accentColor={accentColor}>
                                    <BulletContent>
                                        <BulletTitle>{bulletTitle}</BulletTitle>
                                        {bulletDesc}
                                    </BulletContent>
                                </BulletItem>
                            );
                        }

                        return (
                            <BulletItem key={index} accentColor={accentColor}>
                                <BulletContent>{cleanText}</BulletContent>
                            </BulletItem>
                        );
                    })}
                </BulletList>
            );
        }

        return paragraphs?.map((para, index) => (
            <Paragraph key={index}>{para}</Paragraph>
        ));
    };

    return (
        <Section>
            <SectionTitle accentColor={accentColor}>{title}</SectionTitle>
            <SectionContent>
                {paragraphs && renderContent()}
                {highlight && (
                    <Highlight accentColor={accentColor}>
                        <HighlightText>{highlight}</HighlightText>
                    </Highlight>
                )}
                {children}
            </SectionContent>
        </Section>
    );
};

export default SectionBlock;